import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaGift } from 'react-icons/fa';
import {
  TICKET_RAFFLE_PRESET_SLOTS,
  TICKET_RAFFLE_PRESET_TOTAL_SLOTS,
  TICKET_RAFFLE_PRESET_TOTAL_TRY
} from '../../constants/ticketRafflePreset';

const authHeader = () => {
  const t = localStorage.getItem('adminToken') || localStorage.getItem('userToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const emptySlots = () => Array.from({ length: TICKET_RAFFLE_PRESET_TOTAL_SLOTS }, () => '');
const emptyTicketNumbers = () => Array.from({ length: TICKET_RAFFLE_PRESET_TOTAL_SLOTS }, () => '');

/** Bekleyen kayıtları sabit sıra + tutar ile eşler; uyuşmazsa formları boş döner */
function hydrateSlotsFromPending(pending) {
  if (!Array.isArray(pending) || pending.length !== TICKET_RAFFLE_PRESET_TOTAL_SLOTS) return emptySlots();
  const next = [...emptySlots()];
  let ok = true;
  const sortedPreset = TICKET_RAFFLE_PRESET_SLOTS.map((s) => s.amountTry);
  for (let i = 0; i < TICKET_RAFFLE_PRESET_TOTAL_SLOTS; i++) {
    const p = pending[i];
    const tryAmt = Number(p?.prizeAmountTry ?? p?.prizeCoins);
    if (tryAmt !== sortedPreset[i]) {
      ok = false;
      break;
    }
    next[i] = String(p.userId ?? '');
  }
  return ok ? next : emptySlots();
}

function hydrateTicketNumsFromPending(pending) {
  if (!Array.isArray(pending) || pending.length !== TICKET_RAFFLE_PRESET_TOTAL_SLOTS) return emptyTicketNumbers();
  const next = [...emptyTicketNumbers()];
  for (let i = 0; i < TICKET_RAFFLE_PRESET_TOTAL_SLOTS; i++) {
    next[i] = String(pending[i]?.winningTicketNumber ?? '');
  }
  return next;
}

const TicketRaffleManager = () => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [raffleBoard, setRaffleBoard] = useState(null);
  const [slotUserIds, setSlotUserIds] = useState(emptySlots);
  const [slotTicketNumbers, setSlotTicketNumbers] = useState(emptyTicketNumbers);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [fetchingBoard, setFetchingBoard] = useState(false);
  const [saving, setSaving] = useState(false);
  const [payingPending, setPayingPending] = useState(false);

  const fetchEvents = useCallback(async () => {
    setLoadingEvents(true);
    try {
      const { data } = await axios.get('/api/tickets/admin/raffle/event-list', { headers: authHeader() });
      setEvents(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    if (events.length > 0 && selectedEvent === null) {
      setSelectedEvent(String(events[0].id));
    }
  }, [events, selectedEvent]);

  const syncRowsFromBoard = useCallback((board) => {
    const pending = board?.pendingAwards;
    const loaded = hydrateSlotsFromPending(pending);
    setSlotUserIds(loaded);
    setSlotTicketNumbers(hydrateTicketNumsFromPending(pending));
  }, []);

  const fetchBoard = useCallback(async () => {
    if (!selectedEvent) {
      setRaffleBoard(null);
      return;
    }
    setFetchingBoard(true);
    try {
      const { data } = await axios.get(`/api/tickets/admin/raffle/${selectedEvent}/state`, {
        headers: authHeader()
      });
      setRaffleBoard(data);
      syncRowsFromBoard(data);
    } catch (e) {
      console.error(e);
      setRaffleBoard(null);
    } finally {
      setFetchingBoard(false);
    }
  }, [selectedEvent, syncRowsFromBoard]);

  useEffect(() => {
    fetchBoard();
  }, [fetchBoard]);

  const eligibilityList =
    raffleBoard?.eligibleUsers?.filter((u) => (Number(u.totalTicketCount) || 0) > 0) || [];

  const setSlot = (idx, userIdStr) => {
    setSlotUserIds((prev) => {
      const copy = [...prev];
      copy[idx] = userIdStr;
      return copy;
    });
  };

  const setSlotTicket = (idx, ticketNoStr) => {
    setSlotTicketNumbers((prev) => {
      const copy = [...prev];
      copy[idx] = ticketNoStr;
      return copy;
    });
  };

  const handleSaveAwards = async () => {
    if (!selectedEvent) return;

    const awards = TICKET_RAFFLE_PRESET_SLOTS.map((slot, idx) => {
      const uid = parseInt(String(slotUserIds[idx] || '').trim(), 10);
      const tno = parseInt(String(slotTicketNumbers[idx] || '').trim(), 10);
      return { userId: uid, prizeAmountTry: slot.amountTry, winningTicketNumber: tno };
    });

    const bad = awards.findIndex((a) => !a.userId || Number.isNaN(a.userId));
    if (bad !== -1) {
      alert(`Satır ${bad + 1} (${TICKET_RAFFLE_PRESET_SLOTS[bad].subtitle}): üye seçin.`);
      return;
    }
    const badT = awards.findIndex((a) => !a.winningTicketNumber || Number.isNaN(a.winningTicketNumber));
    if (badT !== -1) {
      alert(`Satır ${badT + 1} (${TICKET_RAFFLE_PRESET_SLOTS[badT].subtitle}): kazanan bilet numarasını seçin.`);
      return;
    }

    const ids = awards.map((a) => a.userId);
    if (new Set(ids).size !== ids.length) {
      alert('Aynı üyeyi iki ödele seçemezsiniz — her ödül farklı biri olmalı.');
      return;
    }

    const tnums = awards.map((a) => a.winningTicketNumber);
    if (new Set(tnums).size !== tnums.length) {
      alert('Aynı bilet numarası iki ödele seçilemez.');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`/api/tickets/admin/raffle/${selectedEvent}/setup`, { awards }, { headers: authHeader() });
      alert(
        `Tam ${TICKET_RAFFLE_PRESET_TOTAL_SLOTS} kazanan kaydı (${new Intl.NumberFormat('tr-TR').format(TICKET_RAFFLE_PRESET_TOTAL_TRY)} ₺ yapısı) güncellendi.`
      );
      fetchBoard();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.error || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleClosePendingRecords = async () => {
    if (!selectedEvent) return;
    if (
      !window.confirm(
        'Bekleyen çekiliş kayıtlarını tamamlandı olarak işaretlemek üzeresiniz. Sagu Coin yüklenmez. Devam?'
      )
    ) {
      return;
    }
    setPayingPending(true);
    try {
      const res = await axios.post(`/api/tickets/admin/raffle/${selectedEvent}/run`, {}, { headers: authHeader() });
      alert(res.data?.message || 'Tamam.');
      fetchBoard();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.error || 'İşlem tamamlanamadı');
    } finally {
      setPayingPending(false);
    }
  };

  if (loadingEvents) {
    return <div className="p-6 text-white/70">Etkinlikler yükleniyor…</div>;
  }

  return (
    <div className="p-6">
      <h2 className="text-3xl font-bold text-gold mb-2 flex items-center gap-2">
        <FaGift /> Çekiliş yönetimi
      </h2>
      <p className="text-white/65 text-sm mb-6 max-w-3xl leading-relaxed">
        Sabit yapı — toplam <strong className="text-white">{TICKET_RAFFLE_PRESET_TOTAL_TRY.toLocaleString('tr-TR')} ₺</strong>
        :
        bir kişiye 50.000 ₺, bir kişiye 20.000 ₺, bir kişiye 10.000 ₺, dört kişiye 2.500 ₺, on kişiye 1.000 ₺ (
        <strong className="text-white">{TICKET_RAFFLE_PRESET_TOTAL_SLOTS}</strong> ödül). Her satırda bir üye seçin (tekrarsız).
        Çekiliş sayfasında sadece görsel olarak 20+ biletli katılımcılar döner; asıl kazananlar buradır.
      </p>

      <div className="mb-6 flex flex-col sm:flex-row gap-4 sm:items-end">
        <div className="flex-1 max-w-md">
          <label className="block text-white/70 text-sm mb-2">Etkinlik</label>
          <select
            value={selectedEvent || ''}
            onChange={(e) => setSelectedEvent(e.target.value || null)}
            className="w-full px-4 py-3 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
          >
            {events.length === 0 ? (
              <option value="">Etkinlik yok</option>
            ) : (
              events.map((ev) => (
                <option key={ev.id} value={String(ev.id)}>
                  {ev.title}
                </option>
              ))
            )}
          </select>
        </div>
        <button
          type="button"
          disabled={fetchingBoard || !selectedEvent}
          onClick={() => fetchBoard()}
          className="px-4 py-3 bg-navy-600 hover:bg-navy-500 text-white rounded-lg border border-gold/20 disabled:opacity-50"
        >
          Yenile
        </button>
      </div>

      {!selectedEvent || events.length === 0 ? (
        <p className="text-white/50">Önce bilet etkinliği oluşturun.</p>
      ) : (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-amber-900/25 to-navy-800/80 border border-gold/40 rounded-xl p-6">
            {fetchingBoard ? (
              <p className="text-white/60 text-sm">Veri yükleniyor…</p>
            ) : raffleBoard ? (
              <div className="text-white/85 text-sm space-y-1 mb-6">
                <div>
                  Etkinlik: <strong className="text-white">{raffleBoard.eventTitle}</strong>
                </div>
                <div>
                  Onaylı biletli katılımcı: <strong className="text-gold">{eligibilityList.length}</strong>
                </div>
                <div>
                  Bekleyen çekiliş kaydı (hedef {TICKET_RAFFLE_PRESET_TOTAL_SLOTS}):{' '}
                  <strong className="text-gold">{(raffleBoard.pendingAwards || []).length}</strong>
                </div>
                {(raffleBoard.pendingAwards || []).length > 0 ? (
                  <div className="text-amber-200/95 pt-2 border-t border-amber-700/40 mt-2 space-y-2">
                    <button
                      type="button"
                      disabled={payingPending}
                      onClick={handleClosePendingRecords}
                      className="px-4 py-2 rounded-lg bg-amber-700/80 hover:bg-amber-600 text-white text-sm font-semibold border border-amber-500/50 disabled:opacity-50"
                    >
                      {payingPending ? 'İşlem…' : 'Bekleyen çekiliş kayıtlarını kapat'}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="text-white/50 text-sm">Özet alınamadı.</p>
            )}

            <div className="grid xl:grid-cols-2 gap-8">
              <div className="min-w-0">
                <h3 className="text-white font-semibold mb-3 text-sm uppercase tracking-wide text-white/60">
                  Onaylı biletli üyeler
                </h3>
                {!fetchingBoard && raffleBoard && eligibilityList.length === 0 && (
                  <p className="text-amber-200/95 text-sm py-4 bg-amber-900/20 border border-amber-600/30 rounded-lg px-3">
                    Onaylı biletli üye yok — önce biletleri onaylayın.
                  </p>
                )}
                {!fetchingBoard && raffleBoard && eligibilityList.length > 0 && (
                  <div className="overflow-x-auto rounded-lg border border-gold/20 bg-navy-900/40 max-h-96 overflow-y-auto">
                    <table className="w-full text-xs sm:text-sm">
                      <thead className="sticky top-0 bg-navy-800/95 text-white/80">
                        <tr>
                          <th className="px-2 py-2 text-left">ID</th>
                          <th className="px-2 py-2 text-left">Üye</th>
                          <th className="px-2 py-2 text-left">Bilet</th>
                        </tr>
                      </thead>
                      <tbody className="text-white">
                        {eligibilityList.map((u) => (
                          <tr key={u.userId} className="border-t border-gold/10">
                            <td className="px-2 py-1.5 font-mono text-gold">{u.userId}</td>
                            <td className="px-2 py-1.5 font-medium">{u.username}</td>
                            <td className="px-2 py-1.5">{u.totalTicketCount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="min-w-0">
                <h3 className="text-white font-semibold mb-3 text-sm uppercase tracking-wide text-white/60">
                  Kazananlar (17 ayrı atama)
                </h3>
                <div className="space-y-3 mb-4 max-h-[32rem] overflow-y-auto pe-2">
                  {TICKET_RAFFLE_PRESET_SLOTS.map((slot, idx) => (
                    <div key={idx} className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg bg-navy-900/35 border border-gold/15 px-3 py-2">
                      <div className="shrink-0 w-28 text-gold font-mono text-xs sm:text-sm">#{slot.slotIndex}</div>
                      <div className="shrink-0 w-36 text-amber-200/95 text-xs sm:text-sm font-medium">{slot.subtitle}</div>
                      <select
                        value={slotUserIds[idx] || ''}
                        onChange={(e) => setSlot(idx, e.target.value)}
                        className="flex-1 min-w-0 px-3 py-2 rounded bg-navy-700 border border-gold/30 text-white text-sm"
                      >
                        <option value="">Üye seçin…</option>
                        {eligibilityList.map((u) => (
                          <option key={u.userId} value={String(u.userId)}>
                            #{u.userId} — {u.username} ({u.totalTicketCount} bilet)
                          </option>
                        ))}
                      </select>
                      <select
                        value={slotTicketNumbers[idx] || ''}
                        onChange={(e) => setSlotTicket(idx, e.target.value)}
                        disabled={!slotUserIds[idx]}
                        className="w-full sm:w-44 px-3 py-2 rounded bg-navy-700 border border-gold/30 text-white text-sm disabled:opacity-60"
                      >
                        <option value="">{slotUserIds[idx] ? 'Bilet no…' : 'Önce üye seçin…'}</option>
                        {(() => {
                          const uid = parseInt(String(slotUserIds[idx] || '').trim(), 10);
                          const u = eligibilityList.find((x) => Number(x.userId) === Number(uid));
                          const listRaw = Array.isArray(u?.ticketNumbers) ? u.ticketNumbers : [];
                          const list = [...new Set(listRaw.map((n) => parseInt(n, 10)).filter((n) => !Number.isNaN(n)))].sort(
                            (a, b) => a - b
                          );
                          return list.map((n) => (
                            <option key={n} value={String(n)}>
                              #{n}
                            </option>
                          ));
                        })()}
                      </select>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  disabled={saving || fetchingBoard || !raffleBoard}
                  onClick={handleSaveAwards}
                  className="w-full sm:w-auto px-6 py-3 rounded-lg bg-navy-700 border border-gold/40 hover:bg-navy-600 text-white font-semibold disabled:opacity-45"
                >
                  {saving ? 'Kaydediliyor…' : 'Çekiliş kazananlarını kaydet (17 ödül)'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketRaffleManager;
